import { Input, InputRef, Select } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { FieldError } from '@components/field-error';
import clsx from 'clsx';
import { useSelectCountry } from '../hooks/use-select-country';
import { Country } from '../constants/purchase-number.constant';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';

interface AppSelectCountryProps<T extends FieldValues> {
  required?: boolean;
  label?: string;
  name: Path<T>;
  placeholder?: string;
  control: Control<T>;
  suffixLabel?: string;
  source?: Country[];
  showSearch?: boolean;
  'data-test-id'?: string;
}

export function AppSelectCountry<T extends FieldValues>({ showSearch = true, ...props }: AppSelectCountryProps<T>) {
  const { required, label, name, placeholder, suffixLabel, control, 'data-test-id': dataTestId, ...restProps } = props;
  const { filteredCountries, OnCountrySearch } = useSelectCountry(props?.source && { source: props?.source });
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (dropdownVisible) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [dropdownVisible]);

  return (
    <div className="flex flex-col gap-1.5 w-full">
      <p className="text-base leading-5 font-medium text-text-800">
        {label}
        <span className="text-text-400 text-xs font-normal">{suffixLabel && suffixLabel}</span>{' '}
        {required && <span className="text-red-500">*</span>}
      </p>

      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <div className="w-full flex flex-col gap-1.5" data-test-id={dataTestId}>
            <Select
              className={clsx('w-full')}
              style={{ height: '44px' }}
              getPopupContainer={(triggerNode: HTMLElement) => triggerNode}
              placeholder={placeholder || capitalize(t('search_country'))}
              value={value}
              onChange={onChange}
              status={error && 'error'}
              onOpenChange={(open) => {
                setDropdownVisible(open);
              }}
              popupRender={(menu) => (
                <>
                  {showSearch && <Input ref={inputRef} placeholder={t('find_country')} onChange={OnCountrySearch} />}
                  {menu}
                </>
              )}
              {...restProps}
            >
              {filteredCountries.map((country) => (
                <Select.Option key={country?.uid} value={country?.alphaTwoCode}>
                  <SelectOptionContent
                    flagUrl={country.flagUrl}
                    name={country.name}
                    countryCode={country.alphaTwoCode}
                  />
                </Select.Option>
              ))}
            </Select>
            <FieldError message={error?.message} />
          </div>
        )}
      />
    </div>
  );
}

interface SelectOptionContentProps {
  flagUrl: string;
  name: string;
  countryCode: string;
}

function SelectOptionContent(props: SelectOptionContentProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded-full flex items-center justify-center overflow-hidden bg-surface">
          <img src={props?.flagUrl} alt={t('flag')} className="object-cover w-5 h-5" />
        </div>
        <span className="font-semibold text-text-800">{props.name}</span>
      </div>
      <span className="text-text-400 text-sm font-medium ml-4">{props.countryCode}</span>
    </div>
  );
}
